import React, { useEffect } from "react";
import { Row, Col, Image } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBrain,
  faShieldHalved,
  faFileContract,
  faHardHat,
  faCar,
  faDatabase,
  faNetworkWired,
  faPlug,
  faCogs,
  faKey,
  faCloud,
  faUsersGear,
  faLocationDot,
  faHandshake,
  faBuilding,
  faCalendarCheck,
  faArrowRight,
  faServer
} from "@fortawesome/free-solid-svg-icons";
import { faAws } from "@fortawesome/free-brands-svg-icons";
import { faCircleCheck, faCalendarDays } from "@fortawesome/free-regular-svg-icons";
import AOS from "aos";
import "aos/dist/aos.css";
import Title from "../../components/Title/Title";
import GoogleCalendarPopup, {
  GOOGLE_CALENDAR_FEASIBILITY_AUDIT_URL,
  GOOGLE_CALENDAR_GENERAL_URL
} from "../../components/GoogleCalendarPopup/GoogleCalendarPopup";
import "./AboutOilAndGas.scss";

const provenCaseHighlights = [
  {
    logo: require("../../assets/brands/AXPC.png"),
    client: "American Exploration and Production Council (AXPC)",
    category: "Upstream Intelligence & AI Benchmarking Platform (Amazon Bedrock)",
    summary:
      "Architected and developed the comprehensive AXPC member intelligence platform and enterprise AI Assistant on AWS for the premier US exploration and production council.",
    keyFeatures: [
      {
        title: "AXPC AI Assistant (Amazon Bedrock)",
        description:
          "Custom AWS Bedrock RAG assistant with conversational citation verification across internal policy positions, regulatory filings, and energy datasets."
      },
      {
        title: "Upstream Safety (SIF) Benchmarking",
        description:
          "Multi-operator tracking of Serious Injury or Fatality metrics, IOGP life-saving rule violations, and causal analysis across US upstream operations."
      },
      {
        title: "ESG & Emissions Analytics Engine",
        description:
          "Interactive benchmarking for greenhouse gas emissions (EPA Subpart W), Responsibly Sourced Gas (RSG) certification, and TCFD maturity."
      },
      {
        title: "Enterprise Multi-Tenant AWS Security",
        description:
          "Role-based access control (RBAC), Cognito authentication, and strict cryptographic isolation of proprietary operator data."
      }
    ]
  },
  {
    logo: require("../../assets/brands/VG.png"),
    client: "Venture Global LNG",
    category: "Industrial Terminal Logistics & Operations Platform (Serverless AWS)",
    summary:
      "Architected and deployed a mission-critical, cloud-native operational logistics and facility access platform for Venture Global LNG export terminal operations.",
    keyFeatures: [
      {
        title: "Real-Time Terminal Logistics & GIS Mapping",
        description:
          "Interactive mapping and live capacity tracking across terminal parking lots and security checkpoints with automated threshold alerting."
      },
      {
        title: "High-Volume Contractor Credentialing",
        description:
          "Bulk import, verification, and automated parking and gate permissions management for thousands of third-party contractor personnel."
      },
      {
        title: "Mobile Gate Attendant PWA",
        description:
          "Progressive Web Application (PWA) designed for security personnel and field attendants stationed at physical gates and remote staging lots."
      },
      {
        title: "Serverless AWS Architecture",
        description:
          "High-availability cloud infrastructure built on AWS SAM, Lambda, DynamoDB, and Cognito with automated contractor notification dispatch."
      }
    ]
  }
];

const specificOgCapabilities = [
  {
    id: "secure-ai-assistants",
    icon: faBrain,
    tag: "AI & Bedrock",
    title: "Secure AI Assistants",
    description:
      "Single-tenant AWS Bedrock RAG assistants indexing technical manuals, wellbore dossiers, operating procedures, and regulatory filings with zero data leakage and zero public LLM training."
  },
  {
    id: "hse-safety-benchmarking",
    icon: faHardHat,
    tag: "Upstream HSE",
    title: "Upstream HSE Safety Benchmarking",
    description:
      "Multi-operator Serious Injury or Fatality (SIF) tracking, IOGP Life-Saving Rules compliance analytics, and root-cause classification engines to benchmark upstream safety trends."
  },
  {
    id: "terminal-logistics",
    icon: faCar,
    tag: "Midstream & LNG",
    title: "Terminal Logistics & Gate Systems",
    description:
      "GIS-enabled parking and staging capacity tracking, high-volume contractor credentialing pipelines, and field-ready mobile PWAs for industrial export terminals and plants."
  },
  {
    id: "air-gapped-cloud",
    icon: faCloud,
    tag: "Cloud Security",
    title: "Air-Gapped & BYOC Cloud Software",
    description:
      "“Bring Your Own Cloud” (BYOC) deployment directly inside your corporate AWS VPC / air-gapped environments with Customer-Managed Keys (KMS) and full Terraform automation."
  },
  {
    id: "field-ticket-ingestion",
    icon: faFileContract,
    tag: "Field Automation",
    title: "Automated Field Ticket & AFE Ingestion",
    description:
      "AI document parsing pipelines extracting structured line items, water hauling slips, trucking manifests, and AFE codes from PDF and handwritten tickets with human-in-the-loop review."
  },
  {
    id: "scada-telemetry-ingestion",
    icon: faNetworkWired,
    tag: "Edge & SCADA",
    title: "Legacy SCADA & Historian Ingestion",
    description:
      "High-throughput streaming pipelines connecting legacy RTUs, OSIsoft PI historians, and MQTT field brokers to AWS cloud analytics and real-time operational alert dashboards."
  }
];

const awsSecurityControls = [
  {
    icon: faBrain,
    control: "LLM Engine",
    tech: "Amazon Bedrock",
    description:
      "Models hosted via Bedrock (Claude 3.5, Llama 3, Amazon Titan) explicitly do not use customer inputs or outputs to train foundation models. All data is encrypted in transit and at rest."
  },
  {
    icon: faNetworkWired,
    control: "Network Isolation",
    tech: "AWS PrivateLink + VPC",
    description:
      "Routes traffic strictly within your private Virtual Private Cloud (VPC) over the dedicated AWS backbone. No proprietary traffic ever traverses the public internet."
  },
  {
    icon: faKey,
    control: "Data Encryption",
    tech: "AWS KMS (Customer Managed Keys)",
    description:
      "Data, vector databases, and model caches are encrypted using Customer Managed Keys (CMK) that your organization strictly controls and can revoke at any time."
  },
  {
    icon: faDatabase,
    control: "Vector Indexing",
    tech: "Amazon OpenSearch Serverless / pgvector",
    description:
      "Indexes well logs, field tickets, and financial records securely inside your isolated VPC boundary with zero exposure to external services."
  },
  {
    icon: faUsersGear,
    control: "Identity & Access",
    tech: "AWS IAM + IAM Identity Center",
    description:
      "Integrates directly with your corporate SSO (Microsoft Entra ID / Azure AD, Okta) for strict role-based access control (RBAC) and facility-level permissions."
  }
];

const ecosystemIntegrations = [
  {
    name: "AWS Energy Data Insights",
    vendor: "Amazon Web Services (OSDU)",
    icon: faAws,
    isBrand: true,
    badge: "Subsurface & OSDU",
    description:
      "Native compatibility with Open Subsurface Data Universe (OSDU) standards for seismic, well log, and reservoir data platforms on AWS."
  },
  {
    name: "WellView & SiteView",
    vendor: "Peloton",
    icon: faDatabase,
    badge: "Well Lifecycle",
    description:
      "Automated extraction and sync of daily drilling reports, casing logs, completions dossiers, and morning wellsite summaries."
  },
  {
    name: "OpenInvoice",
    vendor: "Enverus",
    icon: faFileContract,
    badge: "Field Tickets & AFE",
    description:
      "AI-driven parsing of field tickets, 3-way matching (PO, ticket, invoice), AFE budget validation, and rate sheet compliance."
  },
  {
    name: "SCADA & Field Historians",
    vendor: "OSIsoft PI / Ignition / OPC-UA",
    icon: faNetworkWired,
    badge: "Real-Time Telemetry",
    description:
      "High-throughput edge pipelines bridging legacy RTUs, Modbus, MQTT, and OSIsoft PI historians into live AWS monitoring dashboards."
  },
  {
    name: "AWS IoT SiteWise & Industrial Edge",
    vendor: "Amazon Web Services",
    icon: faCogs,
    badge: "Industrial IoT & Edge",
    description:
      "Asset modeling and real-time ingestion from field edge devices, remote pump jacks, compressors, and pipeline flow meters directly into AWS Data Lakes."
  },
  {
    name: "Microsoft Entra ID (Azure AD)",
    vendor: "Microsoft / Okta SSO",
    icon: faPlug,
    badge: "Identity Federation",
    description:
      "Seamless enterprise identity federation into your AWS-hosted application, providing single sign-on and centralized governance."
  }
];

export default function AboutOilAndGas() {
  useEffect(() => {
    AOS.init();
    window.scrollTo(0, 0);

    const prevTitle = document.title;
    document.title = "WDT | Custom Energy Software & AI in The Woodlands, TX";

    const setMetaTag = (selector, attr, val, fallbackName) => {
      let el = document.querySelector(selector);
      let created = false;
      if (!el) {
        el = document.createElement("meta");
        if (selector.startsWith('meta[property=')) {
          el.setAttribute("property", fallbackName);
        } else {
          el.setAttribute("name", fallbackName);
        }
        document.head.appendChild(el);
        created = true;
      }
      const prevVal = el.getAttribute(attr);
      el.setAttribute(attr, val);
      return { el, attr, prevVal, created };
    };

    const ogDesc =
      "Trusted by AXPC & Venture Global LNG. Custom AI assistants, upstream HSE benchmarking, terminal logistics, and air-gapped AWS software built in The Woodlands, TX.";
    const ogTitle = "WDT | Energy Software & AI Partner in The Woodlands, TX";

    const tags = [
      setMetaTag('meta[name="description"]', 'content', ogDesc, 'description'),
      setMetaTag('meta[property="og:title"]', 'content', ogTitle, 'og:title'),
      setMetaTag('meta[property="og:description"]', 'content', ogDesc, 'og:description'),
      setMetaTag('meta[property="og:url"]', 'content', 'https://wdttx.com/oil-and-gas', 'og:url'),
      setMetaTag('meta[property="og:image"]', 'content', 'https://wdttx.com/og-oil-and-gas.jpg', 'og:image'),
      setMetaTag('meta[property="og:image:width"]', 'content', '1200', 'og:image:width'),
      setMetaTag('meta[property="og:image:height"]', 'content', '630', 'og:image:height'),
      setMetaTag('meta[name="twitter:title"]', 'content', ogTitle, 'twitter:title'),
      setMetaTag('meta[name="twitter:description"]', 'content', ogDesc, 'twitter:description'),
      setMetaTag('meta[name="twitter:image"]', 'content', 'https://wdttx.com/og-oil-and-gas.jpg', 'twitter:image'),
    ];

    return () => {
      document.title = prevTitle;
      tags.forEach(({ el, attr, prevVal, created }) => {
        if (created) {
          el.remove();
        } else if (prevVal !== null) {
          el.setAttribute(attr, prevVal);
        }
      });
    };
  }, []);


  return (
    <div id="oil_and_gas_page" className="oil-and-gas-page-wrapper">
      {/* 1. HERO SECTION: Headline, Woodlands Trust Anchor, and Top Proof-of-Work */}
      <div
        className="hero-area overlay"
        style={{
          backgroundImage: `url(${require("../../assets/oil-and-gas/hero-oil-and-gas.jpg")})`
        }}
      >
        <div className="container">
          <div className="block" data-aos="fade-down" data-aos-duration="600">
            {/* Trust Anchor Eyebrow */}
            <div className="hero-eyebrow mb-3">
              <span className="badge-energy-pill">
                <FontAwesomeIcon icon={faLocationDot} className="me-2 text-warning" />
                THE WOODLANDS, TEXAS &bull; HOUSTON ENERGY CORRIDOR
              </span>
            </div>

            <h1>Mission-Critical Energy Software &amp; Private AI on AWS</h1>
            <p className="hero-subheading mb-4">
              Partner with a dedicated energy software agency founded and led right from The Woodlands, TX. While our engineering team operates remotely to deliver high velocity, our CEO &amp; Owner is based locally and personally meets in person with nearby Houston-area energy executives.
            </p>

            {/* TOP "PROOF OF WORK" HIGHLIGHT STRIP */}
            <div className="top-proof-banner p-3 p-md-4 mb-4 rounded shadow-lg text-start">
              <div className="row align-items-center g-3">
                <div className="col-12 col-lg-8">
                  <div className="proof-statement-lead d-flex align-items-start">
                    <FontAwesomeIcon icon={faShieldHalved} className="proof-shield-icon me-3 mt-1 flex-shrink-0" />
                    <div>
                      <span className="proof-kicker d-block">PROVEN PROOF OF WORK</span>
                      <strong className="proof-quote">
                        “Trusted by the American Exploration and Production Council (AXPC) and Venture Global LNG to architect secure, mission-critical energy software.”
                      </strong>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-lg-4">
                  <div className="d-flex align-items-center justify-content-center justify-content-lg-end gap-3 bg-white p-2 rounded client-logo-pill">
                    <Image
                      src={require("../../assets/brands/AXPC.png")}
                      alt="AXPC Logo"
                      className="hero-partner-logo"
                    />
                    <div className="logo-separator" />
                    <Image
                      src={require("../../assets/brands/VG.png")}
                      alt="Venture Global LNG Logo"
                      className="hero-partner-logo"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Primary Action Buttons */}
            <div className="d-flex justify-content-center flex-wrap gap-3 mt-4">
              <GoogleCalendarPopup
                id="btn_hero_inperson_offer"
                url={GOOGLE_CALENDAR_GENERAL_URL}
                className="btn-cta-primary"
                modalTitle="Book In-Person Strategy Session with our CEO"
              >
                <FontAwesomeIcon icon={faHandshake} className="me-2" />
                Book In-Person Strategy Session with our CEO
              </GoogleCalendarPopup>
              <GoogleCalendarPopup
                id="link_hero_feasibility_audit"
                url={GOOGLE_CALENDAR_FEASIBILITY_AUDIT_URL}
                label="Schedule 15-Minute Technical Audit"
                className="btn-cta-secondary"
              />
            </div>
          </div>
        </div>
      </div>

      {/* 2. HEAVYWEIGHT CASE STUDIES (FEATURED AT THE TOP) */}
      <div className="section py-5 bg-white energy-track-record-section" id="caseStudies">
        <div className="container py-4">
          <Row className="py-3">
            <Col xs={12} data-aos="fade-up" data-aos-duration="500">
              <div className="text-center mb-3">
                <span className="section-pretitle">ENTERPRISE TRACK RECORD</span>
                <Title regular_text="Heavyweight" highlighted_text="Case Studies" />
                <p className="text-muted text-center mx-auto lead-subtext mt-2" style={{ maxWidth: "820px" }}>
                  Proven engineering solutions trusted by top US energy operators and industry councils to solve mission-critical operational challenges.
                </p>
              </div>
            </Col>
          </Row>
          <Row className="g-4 justify-content-center">
            {provenCaseHighlights.map((c, i) => (
              <Col key={i} xs={12} lg={6} data-aos="fade-up" data-aos-duration="500" data-aos-delay={i * 150}>
                <div className="energy-case-card h-100 p-4 p-md-5 rounded shadow-sm border bg-white">
                  <div className="d-flex align-items-center mb-4">
                    <Image
                      src={c.logo}
                      alt={c.client}
                      style={{ maxHeight: "55px", maxWidth: "160px", objectFit: "contain" }}
                      className="me-3"
                    />
                    <div>
                      <h4 className="fw-bold mb-1 text-dark">{c.client}</h4>
                      <span className="case-category">{c.category}</span>
                    </div>
                  </div>
                  <p className="case-summary">{c.summary}</p>
                  <ul className="list-unstyled mb-0 feature-list">
                    {c.keyFeatures.map((feat, idx) => (
                      <li key={idx} className="mb-3 d-flex align-items-start">
                        <FontAwesomeIcon icon={faCircleCheck} className="text-primary mt-1 me-3 flex-shrink-0 fs-6" />
                        <div>
                          <span className="feature-title d-block">{feat.title}</span>
                          <span className="feature-desc d-block">{feat.description}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* 3. SPECIFIC DOMAIN-FOCUSED O&G CAPABILITIES */}
      <div
        className="section py-5 og-domain-capabilities-section"
        id="domainCapabilities"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.92), rgba(15, 23, 42, 0.95)), url(${require("../../assets/oil-and-gas/modern-plant.jpg")})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundAttachment: "fixed"
        }}
      >
        <div className="container py-4">
          <Row className="py-3">
            <Col xs={12} data-aos="fade-up" data-aos-duration="500">
              <div className="text-center">
                <span className="section-pretitle">BEYOND GENERIC APP DEVELOPMENT</span>
                <Title regular_text="Domain-Specific" highlighted_text="O&amp;G Capabilities" />
                <p className="text-center text-light mx-auto lead-subtext mt-2" style={{ maxWidth: "800px" }}>
                  We don't build generic web pages. We build specialized, secure, high-throughput software architectures tailored to upstream E&amp;P workflows, midstream logistics, and enterprise energy compliance.
                </p>
              </div>
            </Col>
          </Row>
          <Row className="g-4 justify-content-center">
            {specificOgCapabilities.map((uc, index) => (
              <Col
                key={uc.id}
                xs={12}
                md={6}
                lg={4}
                data-aos="fade-up"
                data-aos-duration="500"
                data-aos-delay={index * 80}
              >
                <div className="og-capability-card h-100 p-4 rounded shadow bg-white border">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="capability-icon-wrap">
                      <FontAwesomeIcon icon={uc.icon} />
                    </div>
                    <span className="capability-badge">{uc.tag}</span>
                  </div>
                  <h4 className="capability-title mb-2">{uc.title}</h4>
                  <p className="capability-desc mb-3">{uc.description}</p>
                  <div className="mt-auto">
                    <GoogleCalendarPopup
                      id={`btn_capability_${uc.id}`}
                      url={GOOGLE_CALENDAR_GENERAL_URL}
                      className="btn-capability-link"
                      modalTitle={`Discuss Architecture: ${uc.title}`}
                    >
                      <span>Discuss Architecture</span>
                      <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                    </GoogleCalendarPopup>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* 4. TRUST ANCHOR: THE WOODLANDS & LOCAL TEXAS LEADERSHIP (WHITE BACKGROUND) */}
      <div className="section local-trust-anchor-section py-5 bg-white text-dark" id="woodlandsAdvantage">
        <div className="container py-4">
          <Row className="align-items-center g-5">
            <Col xs={12} lg={6} data-aos="fade-right" data-aos-duration="500">
              <div className="trust-anchor-content">
                <div className="d-flex align-items-center mb-3">
                  <div className="local-pin-icon me-3">
                    <FontAwesomeIcon icon={faLocationDot} />
                  </div>
                  <div>
                    <span className="section-pretitle">LOCAL TEXAS LEADERSHIP</span>
                    <h2 className="text-dark fw-bold mb-0">Executive Presence in The Woodlands, TX</h2>
                  </div>
                </div>
                <p className="lead text-dark mb-3" style={{ lineHeight: "1.7" }}>
                  Energy companies value true local accountability and face-to-face leadership. When you work with <strong>Woodlands Digital Technologies</strong>, you get high-velocity engineering backed by an executive based right here in your backyard.
                </p>
                <p className="text-muted mb-4" style={{ lineHeight: "1.6" }}>
                  While our dedicated engineering team operates remotely to deliver rapid, cost-efficient builds, <strong>our CEO &amp; Owner is located in The Woodlands and personally attends all in-person meetings, discovery sessions, and architecture reviews</strong> across The Woodlands, Greenspoint, the Energy Corridor, and Downtown Houston.
                </p>

                <div className="row g-3 pt-2">
                  <div className="col-12 col-sm-6">
                    <div className="local-pillar-box p-3 rounded border bg-light">
                      <div className="d-flex align-items-center mb-2">
                        <FontAwesomeIcon icon={faHandshake} className="text-primary me-2 fs-5" />
                        <strong className="text-dark">CEO In-Person Meetings</strong>
                      </div>
                      <span className="text-muted small d-block">
                        Our CEO/Owner meets face-to-face with nearby clients across Greater Houston &amp; The Woodlands.
                      </span>
                    </div>
                  </div>
                  <div className="col-12 col-sm-6">
                    <div className="local-pillar-box p-3 rounded border bg-light">
                      <div className="d-flex align-items-center mb-2">
                        <FontAwesomeIcon icon={faServer} className="text-primary me-2 fs-5" />
                        <strong className="text-dark">Agile Remote Engineering</strong>
                      </div>
                      <span className="text-muted small d-block">
                        High-velocity cloud &amp; AI developers deploying directly inside your AWS tenant (BYOC).
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-2">
                  <GoogleCalendarPopup
                    id="btn_woodlands_executive_session"
                    url={GOOGLE_CALENDAR_GENERAL_URL}
                    label="Schedule an In-Person Session with our CEO"
                    className="btn-main px-4 py-3 fw-bold me-3"
                    modalTitle="Schedule In-Person Session with our CEO"
                  />
                </div>
              </div>
            </Col>
            <Col xs={12} lg={6} data-aos="fade-left" data-aos-duration="500">
              <div className="position-relative">
                <Image
                  fluid
                  src={require("../../assets/oil-and-gas/energy-plant.jpg")}
                  alt="Woodlands Texas Energy Technology"
                  className="rounded shadow border"
                />
                <div className="local-badge-floating p-3 rounded shadow border">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faBuilding} className="fs-3 text-primary me-3" />
                    <div>
                      <strong className="d-block text-dark">Executive Office &bull; The Woodlands, TX</strong>
                      <span className="text-muted small">CEO In-Person Consultations for Houston-Area Operators</span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* 5. DATA SECURITY & PRIVACY GUARANTEE: AWS Controls & BYOC Architecture (DARK IMAGE BACKGROUND) */}
      <div
        className="security-guarantee-section py-5"
        id="dataSecurity"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.93), rgba(15, 23, 42, 0.96)), url(${require("../../assets/oil-and-gas/energy-plant.jpg")})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundAttachment: "fixed"
        }}
      >
        <div className="container py-4">
          <div className="security-callout-card p-4 p-md-5 rounded shadow-lg" data-aos="fade-up" data-aos-duration="500">
            <Row className="align-items-center mb-4">
              <Col xs={12} lg={8}>
                <div className="d-flex align-items-center mb-3">
                  <div className="security-shield-icon me-3">
                    <FontAwesomeIcon icon={faShieldHalved} />
                  </div>
                  <div>
                    <span className="security-kicker">ENTERPRISE AWS DATA PRIVACY ARCHITECTURE</span>
                    <h2 className="security-title text-white mb-0">Air-Gapped &amp; Private Tenant Security Guarantee</h2>
                  </div>
                </div>
                <p className="security-lead text-light mb-0">
                  <strong className="text-white">Private Tenant &amp; BYOC Deployment on AWS.</strong> Your proprietary subsurface, wellbore, HSE, and financial records never leave your environment and are never used to train public LLMs.
                </p>
              </Col>
              <Col xs={12} lg={4} className="text-lg-end mt-3 mt-lg-0">
                <GoogleCalendarPopup
                  id="link_security_audit_cta"
                  url={GOOGLE_CALENDAR_GENERAL_URL}
                  label="Review AWS Architecture"
                  className="fw-bold px-4 py-2 btn-main"
                />
              </Col>
            </Row>

            {/* AWS Implementation Grid */}
            <hr className="my-4 security-divider" />
            <h4 className="text-white mb-3 fw-bold fs-5">
              <FontAwesomeIcon icon={faAws} className="text-primary me-2" />
              AWS Implementation of Enterprise Privacy Controls
            </h4>
            <Row className="g-3 mb-4">
              {awsSecurityControls.map((item, idx) => (
                <Col key={idx} xs={12} md={6} lg={4} data-aos="fade-up" data-aos-duration="500" data-aos-delay={idx * 80}>
                  <div className="security-pillar-box h-100 p-3 bg-white rounded shadow-sm">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="pillar-control-badge">{item.control}</span>
                      <div className="pillar-icon">
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                    </div>
                    <h5 className="pillar-title">{item.tech}</h5>
                    <p className="pillar-desc mb-0">{item.description}</p>
                  </div>
                </Col>
              ))}
            </Row>

            {/* BYOC (Bring Your Own Cloud) Strategy Card */}
            <div className="byoc-strategy-banner p-4 rounded mt-4">
              <Row className="align-items-center">
                <Col xs={12} lg={7}>
                  <div className="d-flex align-items-center mb-2">
                    <FontAwesomeIcon icon={faCloud} className="text-primary me-2 fs-4" />
                    <h4 className="mb-0 text-white fw-bold">Preferred by Energy Operators: "Bring Your Own Cloud" (BYOC)</h4>
                  </div>
                  <p className="mb-0 text-light small" style={{ lineHeight: "1.6" }}>
                    <strong>"Our software runs inside your AWS tenant."</strong> We provide Terraform and AWS CloudFormation templates to deploy directly into your existing corporate AWS accounts. Your data never touches third-party SaaS servers, giving your IT and cybersecurity leadership 100% control over KMS encryption keys, CloudTrail audit logs, and VPC security boundaries.
                  </p>
                </Col>
                <Col xs={12} lg={5} className="mt-3 mt-lg-0">
                  <div className="d-flex flex-column gap-2">
                    <div className="byoc-option p-2 rounded">
                      <strong className="text-white d-block small">1. BYOC Deployment (Client-Hosted)</strong>
                      <span className="text-muted small">Deployed via Terraform directly into your corporate AWS account.</span>
                    </div>
                    <div className="byoc-option p-2 rounded">
                      <strong className="text-white d-block small">2. Dedicated Single-Tenant AWS (WDT-Managed)</strong>
                      <span className="text-muted small">Hosted in an isolated AWS account provisioned exclusively for your team.</span>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </div>

      {/* 6. ECOSYSTEM & INTEGRATION COMPATIBILITY SECTION (WHITE BACKGROUND) */}
      <div className="section py-5 bg-white" id="energyIntegrations">
        <div className="container py-4">
          <Row className="py-3">
            <Col xs={12} data-aos="fade-up" data-aos-duration="500">
              <div className="text-center">
                <span className="section-pretitle">ENTERPRISE COMPATIBILITY</span>
                <Title regular_text="Ecosystem &amp;" highlighted_text="Integration Stack" />
                <p className="text-center text-muted mx-auto lead-subtext mt-2" style={{ maxWidth: "780px" }}>
                  Built to connect directly into standard Houston energy enterprise stacks, subsurface data standards (OSDU), and corporate identity providers without disrupting ongoing operations.
                </p>
              </div>
            </Col>
          </Row>
          <Row className="g-4 justify-content-center">
            {ecosystemIntegrations.map((stack, idx) => (
              <Col key={idx} xs={12} md={6} lg={4} data-aos="fade-up" data-aos-duration="500" data-aos-delay={idx * 100}>
                <div className="integration-card h-100 p-4 rounded shadow-sm bg-white border">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="integration-icon-wrap">
                      <FontAwesomeIcon icon={stack.icon} />
                    </div>
                    <span className="integration-badge">{stack.badge}</span>
                  </div>
                  <h4 className="integration-name mb-1">{stack.name}</h4>
                  <div className="integration-vendor mb-3">{stack.vendor}</div>
                  <p className="integration-desc mb-0">{stack.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* 7. STEP 2: FRICTIONLESS CONVERSION - THE "LOCAL" OFFER WITH SINGLE FREE CONSULTATION BOOKING (DARK IMAGE BACKGROUND) */}
      <div
        className="section py-5 local-offer-section"
        id="consultationOffer"
        style={{
          backgroundImage: `linear-gradient(rgba(10, 15, 30, 0.93), rgba(15, 23, 42, 0.96)), url(${require("../../assets/oil-and-gas/modern-plant.jpg")})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundAttachment: "fixed"
        }}
      >
        <div className="container py-4">
          <Row className="justify-content-center">
            <Col xs={12} lg={10} data-aos="fade-up" data-aos-duration="500">
              <div className="text-center mb-4">
                <span className="badge-local-offer mb-2">
                  <FontAwesomeIcon icon={faHandshake} className="me-2" />
                  THE LOCAL TEXAS OFFER &bull; HOUSTON METRO &amp; THE WOODLANDS
                </span>
                <Title regular_text="Free Strategy Consultation" highlighted_text="With Our CEO" />
                <p className="lead text-light mx-auto mt-2" style={{ maxWidth: "800px" }}>
                  Energy executives value direct access to decision-makers. Schedule a <strong>100% free consultation</strong> directly with our CEO &amp; Owner using our calendar link below—available in person at your Houston-area office or via video conference.
                </p>
              </div>
            </Col>
          </Row>

          <Row className="justify-content-center g-4">
            {/* Left Column: What We Cover in the Free Consultation */}
            <Col xs={12} lg={6} data-aos="fade-right" data-aos-duration="500">
              <div className="strategy-session-card p-4 p-md-4 rounded h-100 shadow-lg bg-white border">
                <h4 className="fw-bold mb-3 text-dark">
                  <FontAwesomeIcon icon={faCalendarDays} className="text-primary me-2" />
                  What We Cover in Your Free Session
                </h4>
                <p className="text-muted small mb-4">
                  A high-value, no-obligation technical working session directly with our CEO and lead energy software architects—not sales reps:
                </p>

                <div className="strategy-agenda-list">
                  <div className="agenda-item d-flex align-items-start mb-3">
                    <div className="agenda-num me-3">1</div>
                    <div>
                      <strong className="d-block text-dark">Understand Your Operations &amp; Pain Points</strong>
                      <span className="text-muted small">
                        Review your current workflows across field ticketing, SCADA telemetry, HSE reporting, or terminal logistics.
                      </span>
                    </div>
                  </div>

                  <div className="agenda-item d-flex align-items-start mb-3">
                    <div className="agenda-num me-3">2</div>
                    <div>
                      <strong className="d-block text-dark">Brainstorm Custom Software &amp; AI Solutions</strong>
                      <span className="text-muted small">
                        Explore architecture options including private Bedrock AI assistants, air-gapped VPCs, or custom web/mobile PWAs.
                      </span>
                    </div>
                  </div>

                  <div className="agenda-item d-flex align-items-start mb-3">
                    <div className="agenda-num me-3">3</div>
                    <div>
                      <strong className="d-block text-dark">Provide High-Level Approximate Quote &amp; Timeline</strong>
                      <span className="text-muted small">
                        Get transparent budget estimates, MVP delivery milestones (typically 6–8 weeks), and deployment roadmaps.
                      </span>
                    </div>
                  </div>

                  <div className="agenda-item d-flex align-items-start">
                    <div className="agenda-num me-3">4</div>
                    <div>
                      <strong className="d-block text-dark">Evaluate BYOC &amp; Security Compliance</strong>
                      <span className="text-muted small">
                        Assess Terraform deployment within your corporate AWS tenant, KMS key management, and SSO integration.
                      </span>
                    </div>
                  </div>
                </div>

                <div className="local-location-badge p-3 rounded mt-4 bg-light border">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faLocationDot} className="text-danger fs-4 me-3" />
                    <div>
                      <strong className="d-block text-dark small">Where Our CEO Meets You:</strong>
                      <span className="text-muted small">
                        At your office in The Woodlands / Houston metro area, or via Microsoft Teams / Google Meet.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            {/* Right Column: SINGLE FREE CONSULTATION ACTION CARD */}
            <Col xs={12} lg={6} data-aos="fade-left" data-aos-duration="500">
              <div className="strategy-action-card p-4 p-md-5 rounded shadow-lg bg-white border h-100 d-flex flex-column justify-content-between">
                <div>
                  <div className="d-flex align-items-center mb-3">
                    <div className="action-card-icon me-3">
                      <FontAwesomeIcon icon={faCalendarCheck} />
                    </div>
                    <div>
                      <span className="text-primary text-uppercase fw-bold letter-spacing-1 small">DIRECT CALENDAR BOOKING</span>
                      <h4 className="fw-bold mb-0 text-dark">Book Your Free Consultation</h4>
                    </div>
                  </div>

                  <p className="text-muted mb-4" style={{ lineHeight: "1.6" }}>
                    Click below to open our booking calendar and reserve a free, dedicated strategy consultation directly with our CEO &amp; Owner:
                  </p>

                  <div className="session-highlights-list bg-light p-3 rounded mb-4 border">
                    <div className="d-flex align-items-center mb-2">
                      <FontAwesomeIcon icon={faCircleCheck} className="text-success me-2" />
                      <span className="text-dark small fw-semibold">100% Free &amp; No Obligation (30–45 Mins)</span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <FontAwesomeIcon icon={faCircleCheck} className="text-success me-2" />
                      <span className="text-dark small fw-semibold">In-Person in Greater Houston / Woodlands (or Virtual)</span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <FontAwesomeIcon icon={faCircleCheck} className="text-success me-2" />
                      <span className="text-dark small fw-semibold">Architecture review, feasibility &amp; approximate quote</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faCircleCheck} className="text-success me-2" />
                      <span className="text-dark small fw-semibold">Instant confirmation on our calendar</span>
                    </div>
                  </div>

                  {/* Single Primary Conversion Button */}
                  <div className="booking-cta-buttons mb-3">
                    <GoogleCalendarPopup
                      id="link_consultation_free_cta"
                      url={GOOGLE_CALENDAR_GENERAL_URL}
                      label="Book Free Consultation with Our CEO"
                      className="btn-cta-primary w-100 py-3 shadow-lg fs-6 text-white text-center d-flex justify-content-center align-items-center"
                      modalTitle="Schedule Free Consultation with Our CEO"
                    >
                      <FontAwesomeIcon icon={faHandshake} className="me-2 fs-5" />
                      <span>Book Free Consultation with Our CEO</span>
                    </GoogleCalendarPopup>
                    <small className="text-muted d-block text-center mt-2">
                      Instant confirmation &bull; No sales reps &bull; Strict confidentiality
                    </small>
                  </div>
                </div>

                <div className="pt-3 border-top mt-auto">
                  <div className="d-flex flex-wrap justify-content-between align-items-center">
                    <div className="d-flex align-items-center mb-2 mb-sm-0">
                      <FontAwesomeIcon icon={faLocationDot} className="text-primary me-2" />
                      <span className="small text-muted">
                        Prefer to call directly? <a href="tel:3468002244" className="fw-bold text-dark">(346) 800-2244</a>
                      </span>
                    </div>
                    <span className="badge bg-light text-dark border px-3 py-2 small">
                      <FontAwesomeIcon icon={faCircleCheck} className="text-success me-1" />
                      100% Free
                    </span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}

